import { DestroyRef, Injectable, inject } from '@angular/core';

import { ConfirmService } from '../confirm/confirm.service';

import { HasUnsavedChanges } from './unsaved-changes.types';

@Injectable({ providedIn: 'root' })
export class UnsavedChangesService {
    private readonly confirmService = inject(ConfirmService);
    private readonly registry = new Set<HasUnsavedChanges>();

    constructor() {
        window.addEventListener('beforeunload', this.onBeforeUnload);
    }

    /**
     * Register a component so the browser `beforeunload` warning fires when
     * it has unsaved changes. Auto-unregisters when the component is
     * destroyed via the supplied `DestroyRef`.
     */
    bind(component: HasUnsavedChanges, destroyRef: DestroyRef): void {
        this.registry.add(component);
        destroyRef.onDestroy(() => this.registry.delete(component));
    }

    /**
     * Shows the global "discard changes?" confirm dialog. Resolves to
     * `true` when the user agrees to discard, `false` otherwise. If a
     * component is supplied, the prompt is skipped (auto-resolves `true`)
     * when it reports no unsaved changes.
     */
    confirmDiscard(component?: HasUnsavedChanges): Promise<boolean> {
        if (component && !component.hasUnsavedChanges()) {
            return Promise.resolve(true);
        }
        return this.confirmService
            .ask<true>({
                title: 'common.unsavedChanges.title',
                message: 'common.unsavedChanges.message',
                confirmLabel: 'common.unsavedChanges.confirm',
                cancelLabel: 'common.unsavedChanges.cancel',
                saveLabel: 'common.save',
                saveAction: component?.saveUnsavedChanges?.bind(component),
                variant: 'warning',
                icon: 'warning',
            })
            .then(() => true)
            .catch(() => false);
    }

    private readonly onBeforeUnload = (event: BeforeUnloadEvent): string | undefined => {
        for (const component of this.registry) {
            if (component.hasUnsavedChanges()) {
                event.preventDefault();
                event.returnValue = 'unsaved-changes';
                return 'unsaved-changes';
            }
        }
        return undefined;
    };
}
